import { TableBody, useTheme } from '@mui/material';
import { memo, useMemo } from 'react';
import { Order } from 'shell/view-models/order';
import { BaseTableRows, HeadCell } from './types';
import DotMenu from 'system/components/shared/list-item-cta-menu';
import { PmiTableRow } from './row';
import { ActiveTableActions } from 'system/components/shared/list-item-cta-menu/types';
import { PmiActionTableCell, PmiBodyTableCell } from 'shell/components/shared/table-custom/body';

interface TableBodyProps<T extends BaseTableRows, K extends keyof T> {
  head: Array<HeadCell<T, K>>;
  body: Array<T>;
  page?: number;
  rowsPerPage?: number;
  orders?: Order;
  count?: number;
  ariaLabel?: string;
  onChangePage?: (page: number) => void;
  onChangeRowsPerPage?: (page: number) => void;
  onVisibilityPress?: (cell: T, page?: number) => Promise<T> | void;
  onEditPress?: (cell: T, page?: number) => Promise<T> | void;
  onTrashPress?: (cell: T, page?: number) => Promise<T> | void;
  onSort?: (orders: Order) => void;
  activeActions?: ActiveTableActions;
  isServer?: boolean;
  hideButtons?: boolean;
  hasClientSort?: boolean;
  hasHorizontalScroll?: boolean;
}

const TableBodyComponent = <T extends BaseTableRows, K extends keyof T>({
  body = [],
  head,
  page = 0,
  rowsPerPage = 10,
  onVisibilityPress = (cell) => Promise.resolve(cell),
  onEditPress = (cell) => Promise.resolve(cell),
  onTrashPress = (cell) => Promise.resolve(cell),
  hideButtons = false,
  isServer = false,
  activeActions = {
    read: true,
    create: true,
    edit: true,
    delete: true,
  },
  orders,
  hasClientSort = false,
  hasHorizontalScroll,
}: TableBodyProps<T, K>): React.ReactElement => {
  const theme = useTheme();

  const itemPage = useMemo(() => {
    const firstItem = page * rowsPerPage;
    const lastItem = firstItem + rowsPerPage;
    return {
      firstItem,
      lastItem,
    };
  }, [page, rowsPerPage]);

  const rows = useMemo(() => {
    const sortedElements: Array<T> = hasClientSort ? body : body;
    return isServer ? sortedElements : sortedElements.slice(itemPage.firstItem, itemPage.lastItem);
  }, [isServer, body, itemPage.firstItem, itemPage.lastItem, hasClientSort, orders]);

  const bodyJsx = useMemo(() => {
    return rows.map((row, bodyItemIndex: number) => {
      const id = `table-row-${bodyItemIndex}`;

      return (
        <PmiTableRow
          key={id}
          onClick={() => {
            if (activeActions.read) {
              onVisibilityPress(row);
            }
          }}
          tabIndex={0}
        >
          {head.map((column, headItemIndex) => {
            const labelId = `table-${row[column.id]}-${headItemIndex}`;

            if (column.label) {
              return (
                <PmiBodyTableCell
                  colSpan={column.colSpan}
                  key={labelId}
                  id={labelId}
                  scope="row"
                  classes={{ root: 'notranslate' }}
                  sx={
                    column.id === 'action' || column.overlay
                      ? {
                          position: hasHorizontalScroll ? 'sticky' : 'static',
                          right: 0,
                          zIndex: 10,
                          boxShadow: hasHorizontalScroll ? theme.shadows[16] : 'none',
                          background: theme.palette.background.paper,
                        }
                      : undefined
                  }
                >
                  {row[column.id]}
                </PmiBodyTableCell>
              );
            }
            return null;
          })}

          {!hideButtons ? (
            <PmiActionTableCell
              sx={{
                position: hasHorizontalScroll ? 'sticky' : 'static',
                right: 0,
                zIndex: 10,
                boxShadow: hasHorizontalScroll ? theme.shadows[16] : 'none',
                background: theme.palette.background.paper,
              }}
            >
              <div className="pmi-d-flex">
                <DotMenu
                  onEditPress={() => onEditPress(row)}
                  onTrashPress={() => onTrashPress(row)}
                  onVisibilityPress={() => onVisibilityPress(row)}
                  hidden={row.hidden || {}}
                  active={activeActions}
                />
              </div>
            </PmiActionTableCell>
          ) : null}
        </PmiTableRow>
      );
    });
  }, [rows, head, onVisibilityPress, onEditPress, onTrashPress, hideButtons, hasHorizontalScroll]);

  return <TableBody>{bodyJsx}</TableBody>;
};

TableBodyComponent.displayName = 'Table Body component';

const PmiTableBody = memo(TableBodyComponent);

export default PmiTableBody as typeof TableBodyComponent;
